import React, { useRef, useEffect } from 'react';

interface TweetContentCardProps {
  index: number;
  content: string;
  isEditable: boolean;
  canDelete: boolean;
  onChange: (value: string) => void;
  onDelete: () => void;
}

const TweetContentCard: React.FC<TweetContentCardProps> = ({
  index,
  content,
  isEditable,
  canDelete,
  onChange,
  onDelete,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [content]);

  return (
    <div className="bg-gray-800/50 rounded-xl border-l-4 border-blue-500 shadow-lg overflow-hidden">
      {/* Card Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800/80 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">
            #{index + 1}
          </span>
        </div>
        {isEditable && canDelete && (
          <button
            onClick={onDelete}
            className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-red-400 hover:bg-gray-700 transition-colors"
            title="Delete this content"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        {isEditable ? (
          <textarea
            ref={textareaRef}
            className="w-full bg-gray-900/50 text-white text-sm rounded-lg p-3 border border-gray-600 focus:border-blue-500 focus:outline-none resize-none overflow-hidden"
            style={{ minHeight: '80px' }}
            value={content}
            onChange={(e) => {
              onChange(e.target.value);
              // Auto-resize on change
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            placeholder="Enter tweet content..."
          />
        ) : (
          <div className="text-white text-sm whitespace-pre-wrap leading-relaxed">{content}</div>
        )}
      </div>
    </div>
  );
};

export default TweetContentCard;
